import { Button, DatePicker, Modal, Typography } from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import CurrencySymbol from "@/Components/Common/CurrencySymbol/CurrencySymbol";
import dayjs, { Dayjs } from "dayjs";
import { CellRenderInfo } from "@rc-component/picker/lib/interface";
import { useRecState } from "../../Orchestrator/Recoils/Events/Recoil";
import "../../Components/Feature/Calendar/Calendar.css";
import "../../Components/HyperSales/SaleCard.css";
import { InfoCircleOutlined } from "@ant-design/icons";
import Image from "next/image";
import { _eventBus } from "../../core/events/EventBus";
import { evtTree } from "../../Orchestrator/ConstantTree";
import stopManager from "../../Lib/Utils/StopManager";
import enLocale from "antd/es/date-picker/locale/en_GB";
import arLocale from "antd/es/date-picker/locale/ar_EG";
import { IPassBooking } from "../../Orchestrator/Types/Types/BookingTypes";
import { determinePassengerTypeFromPassenger, getPassengerTypeValueFromType } from "../../Lib/Utils/validationUtil";
import { goToTrainOffers } from "./navigateToTrainOffers";

const { RangePicker } = DatePicker;

interface IProps {
  open: boolean;
  startStop: string;
  endStop: string;
  handleClose: () => void;
  passOffer?: IPassBooking;
}

const SelectDateModal = (props: IProps) => {
  const { open, handleClose, startStop, endStop, passOffer } = props
  const intl = useIntl();

  const [calendarDayInterval] = useRecState<number>("calendarDayIntervalAtom");
  const [calendarRangeBestPrice] = useRecState<any[]>(
    "calendarRangeBestPriceAtom"
  );
  const [selectedRange, setSelectedRange] = useState<[Dayjs | null, Dayjs | null]>([null, null]);

  const handleCancel = () => {
    handleClose()
  }

  const handleDateChange = (dates: any) => {
    setSelectedRange(dates);
  }

  const handleContinue = () => {
    if (!selectedRange[0]) return false

    goToTrainOffers({
      origin: startStop,
      destination: endStop,
      date: selectedRange[0],
      intl,
      passOffer,
    });
  }

  useEffect(() => {
    if (open) {
      _eventBus.emit({
        eventName: evtTree.SelectDateModal.Open,
        data: {
          startStop,
          endStop
        }
      })
    }
  }, [open])

  const priceMap = useMemo(() => {
    return (calendarRangeBestPrice || []).reduce(
      (acc: { [key: string]: number }, item) => {
        const dateKey = dayjs(item.date).format("YYYY-MM-DD");
        acc[dateKey] = item.price;
        return acc;
      },
      {}
    );
  }, [calendarRangeBestPrice]);

  const cellRender = useCallback(
    (current: string | number | Dayjs, info: CellRenderInfo<Dayjs>) => {
      if (
        info.type !== "date" ||
        typeof current !== "object" ||
        !("format" in current)
      ) {
        return info.originNode;
      }

      const dateString = current.format("YYYY-MM-DD");
      const price = priceMap[dateString];

      return (
        <div className="ant-picker-cell-inner">
          <div className="show_date">{current.date()}</div>
          {price != null && (
            <span className="show_price">
              {typeof price === "number"
                ? <><CurrencySymbol /> {price}</>
                : price}
            </span>
          )}
        </div>
      );
    },
    [priceMap]
  );

  const disabledDates = (current: Dayjs) =>
    current.isBefore(dayjs(), "day") ||
    current.isAfter(dayjs().add(calendarDayInterval, "day"), "day");

  const bookingPassengerType = useMemo(() => {
    if (passOffer && passOffer.booking && passOffer.booking.booking && passOffer.booking.booking.passengers && passOffer.booking.booking.passengers.length > 0) {
      let type = determinePassengerTypeFromPassenger(passOffer.booking.booking.passengers[0]);
      if (type) {
        return getPassengerTypeValueFromType(type)
      }
    }
  }, [passOffer])

  return (
    <Modal
      open={open}
      onCancel={handleCancel}
      footer={null}
      closable={true}
      centered={false}
      width={760}
      className="sale-card-modal"
    >
      <Typography.Title
        level={4}
        className="fw-700 text-uppercase ff-wallop fs-24"
      >
        {intl.formatMessage({ id: "selectDateText" })}
      </Typography.Title>

      <div className="trip-trip-info">
        <div>
          <div className="align-items-center">
            <Typography.Text className="fs-16 fw-700">
              {stopManager.getStopNameById(startStop)}
              <Image
                src="/assets/booking/arrow-right.svg"
                width={20}
                height={20}
                alt="one way"
                className="right-arrow"
              />
              {stopManager.getStopNameById(endStop)}
            </Typography.Text>
          </div>
          {
            bookingPassengerType &&
            <div>
              {intl.formatMessage({ id: `passenger.title.${bookingPassengerType}` })}
            </div>
          }
        </div>
      </div>

      <div className="select-range">
        <RangePicker
          locale={intl.locale === "en" ? enLocale : arLocale}
          open={open}
          value={selectedRange}
          onChange={handleDateChange}
          cellRender={cellRender}
          /**
           * Comment because it's call handleDateChange function two time's.
           */
          // onCalendarChange={handleDateChange}
          format="ddd, DD MMM"
          minDate={dayjs()}
          maxDate={dayjs().add(calendarDayInterval, "day")}
          disabled={[false, true]}
          className="range-picker"
          allowEmpty={[false, true]}
          showTime={false}
          popupStyle={{
            display: "block",
          }}
          disabledDate={disabledDates}
          getPopupContainer={(trigger) => {
            const modalBody = document.querySelector(".select-range");
            return modalBody instanceof HTMLElement
              ? modalBody
              : document.body;
          }}
          classNames={{ popup: { root: `change-date-booking home-page-date-picker  ${intl.locale === "en" ? "" : "rtl-range-picker"}` } }}
        />
      </div>

      <div className="range-footer">
        <Typography.Text className="fs-16 fw-600 color-757575 d-flex align-items-center gap-6">
          <InfoCircleOutlined />
          <FormattedMessage id="pricesForOneAdult" />
        </Typography.Text>
        <Button
          type="primary"
          disabled={!selectedRange[0]}
          className="fs-16 radius-12 border-none"
          onClick={() => handleContinue()}
        >
          <FormattedMessage id="continue" />
        </Button>
      </div>
    </Modal>
  )
}

export default SelectDateModal
